import { useEffect, useMemo, useState, type ReactNode } from "react";
import { useQuery } from "@tanstack/react-query";
import { useAuth } from "@/contexts/AuthContext";
import { cn } from "@/lib/utils";
import { SelectorVerComo, type GrupoVerComo } from "@/components/ui/selector-ver-como";
import { AyudaVerComo } from "@/components/admin/AvisoVerComo";
import {
  LIMITE_VER_COMO,
  agruparPorRolPrincipal,
  listarPersonasVerComo,
  notaPersonaVerComo,
  type PersonaVerComo,
} from "@/lib/verComoPersonas";

const ROL_SUPER_ADMIN = 1;

/**
 * "Ver como" por persona, común a los portales: la lista sale de `get_ver_como_personas` (solo
 * quien entra al portal), en bloques por rol principal. Arriba va la vista propia: "Super Admin"
 * para el Super Admin (todos los Super Admin ven lo mismo, así que es la única opción por rol) o
 * "Mi vista" para los demás roles que pueden impersonar. Cada portal guarda la persona elegida en
 * su contexto y la publica al núcleo global; aquí solo se elige.
 */
export function VerComoPersonasSelector({
  rutas,
  seleccionadoEmail,
  seleccionadoNombre,
  onElegir,
  onLimpiar,
  compacto,
  className,
  ayuda,
}: {
  /** Rutas del portal para el LIKE del servidor, p. ej. ["/admin/portal-bancos/%"]. */
  rutas: string[];
  seleccionadoEmail: string | null;
  seleccionadoNombre?: string | null;
  onElegir: (persona: PersonaVerComo) => void;
  onLimpiar: () => void;
  compacto?: boolean;
  className?: string;
  /** Texto extra del tooltip de "Solo lectura" mientras se impersona (p. ej. qué cifras no son de la persona). */
  ayuda?: ReactNode;
}) {
  const { profile } = useAuth();
  const puede = profile?.puede_impersonar === true;
  const esSuperAdmin = profile?.rol_id === ROL_SUPER_ADMIN;

  const [abierto, setAbierto] = useState(false);
  const [busqueda, setBusqueda] = useState("");
  const [busquedaDiferida, setBusquedaDiferida] = useState("");
  useEffect(() => {
    const t = setTimeout(() => setBusquedaDiferida(busqueda), 250);
    return () => clearTimeout(t);
  }, [busqueda]);

  const clave = rutas.join("|");
  const { data: personas = [], isLoading } = useQuery({
    queryKey: ["ver-como-personas", clave, busquedaDiferida],
    queryFn: () => listarPersonasVerComo(rutas, busquedaDiferida),
    enabled: puede && abierto,
    staleTime: 60_000,
  });

  const grupos = useMemo(
    (): GrupoVerComo[] =>
      agruparPorRolPrincipal(personas).map((g) => ({
        titulo: g.rol,
        opciones: g.personas.map((p) => ({
          key: p.email,
          label: p.nombre,
          nota: notaPersonaVerComo(p),
          buscarPor: p.email,
          seleccionada: seleccionadoEmail === p.email,
          onSelect: () => onElegir(p),
        })),
      })),
    [personas, seleccionadoEmail, onElegir],
  );

  if (!puede) return null;

  const impersonando = !!seleccionadoEmail;
  const vistaPropia = esSuperAdmin ? "Super Admin" : "Mi vista";

  return (
    <div className={cn("flex w-full min-w-0 items-center sm:w-auto", className)}>
      {/* "Solo lectura" como prefijo del control (un solo bloque); el detalle va en su tooltip. */}
      <AyudaVerComo detalle={ayuda} />
      <SelectorVerComo
        claseBoton={impersonando ? "rounded-l-none" : undefined}
        etiqueta={impersonando ? seleccionadoNombre || seleccionadoEmail || "Usuario" : vistaPropia}
        impersonando={impersonando}
        opcionPorDefecto={{
          key: "__vista_propia__",
          label: vistaPropia,
          nota: "Por defecto",
          buscarPor: "mi vista super admin",
          seleccionada: !impersonando,
          onSelect: onLimpiar,
        }}
        grupos={grupos}
        onLimpiar={onLimpiar}
        textoVacio="Nadie con acceso a este portal coincide."
        cargando={isLoading}
        busqueda={busqueda}
        onBusquedaChange={setBusqueda}
        onOpenChange={setAbierto}
        pie={personas.length >= LIMITE_VER_COMO ? `Primeros ${LIMITE_VER_COMO}. Escribe un nombre o correo para encontrar a alguien más.` : undefined}
        compacto={compacto}
      />
    </div>
  );
}
